import React from 'react'

import moment from 'moment'

import { Button, Tooltip, Dropdown } from 'antd'

import { Box } from 'common/components/boxes'
import { FloatActionsWrapper } from 'common/components/FloatActionsWrapper'
import { OrderStates, OrderSubStates } from 'common/server/server_types'

import { DownloadOrder } from 'vendor/components/DownloadOrder/download_order'
import { Order } from 'vendor/server/orders'

type OrderActionsProps = {
  order: Order
  updateOrder: (stateChanges: { confirmed_at?: string; rejected_at?: string }) => Promise<void>
  orderDirty: boolean
  isPublic?: boolean
  isFloat?: boolean
  isSubmitting?: boolean
  disabled?: boolean
}

export const OrderActions = ({
  order,
  updateOrder,
  disabled,
  orderDirty,
  isFloat,
  isSubmitting,
}: OrderActionsProps) => {
  const { state, sub_state } = order

  const dropdown = (
    <Dropdown.Button
      menu={{
        items: [{ label: <DownloadOrder orderId={order?.id} isDropdownItem state={state} />, key: '1' }],
      }}
      trigger={['click', 'hover']}
      buttonsRender={([_, rightButton]) => [null, rightButton]}
    />
  )

  if (state == OrderStates.QUOTED) {
    const content = (
      <>
        <Tooltip title="Notify customer you will not provide a quote">
          <Button
            danger
            data-cy="reject-rfq"
            onClick={() => updateOrder({ rejected_at: moment().toISOString(), confirmed_at: null })}
            loading={isSubmitting}
            disabled={disabled}
          >
            Reject RFQ
          </Button>
        </Tooltip>
        <Tooltip title="Notify customer of updates to the RFQ">
          <Button
            type="primary"
            data-cy="respond-rfq"
            onClick={() => updateOrder({})}
            loading={isSubmitting}
            disabled={disabled || !orderDirty}
          >
            Update RFQ
          </Button>
        </Tooltip>
        {dropdown}
      </>
    )

    return isFloat ? (
      <FloatActionsWrapper>{content}</FloatActionsWrapper>
    ) : (
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        {content}
      </Box>
    )
  }

  if (state == OrderStates.CANCELLED) {
    const substate_cancelled =
      sub_state == OrderSubStates.CANCELLED_QUOTE_BY_VENDOR || sub_state == OrderSubStates.CANCELLED_ORDER_BY_VENDOR
    const content = (
      <>
        {substate_cancelled && (
          <Tooltip title="Re-open the request you previously rejected">
            <Button
              data-cy="re-open-request"
              type="primary"
              onClick={() => updateOrder({ rejected_at: null, confirmed_at: null })}
              loading={isSubmitting}
              disabled={disabled}
            >
              Re-Open Request
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Notify customer of updates to the RFQ">
          <Button
            data-cy="update"
            type="primary"
            onClick={() => updateOrder({})}
            loading={isSubmitting}
            disabled={disabled || !orderDirty}
          >
            Update
          </Button>
        </Tooltip>
        {dropdown}
      </>
    )

    return isFloat ? (
      <FloatActionsWrapper>{content}</FloatActionsWrapper>
    ) : (
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        {content}
      </Box>
    )
  }

  if (state == OrderStates.ORDERED && sub_state == OrderSubStates.ORDERED_REQUESTED) {
    const content = (
      <>
        <Tooltip title="Notify customer you have rejected the order">
          <Button
            danger
            data-cy="reject-order"
            onClick={() => updateOrder({ rejected_at: moment().toISOString(), confirmed_at: null })}
            loading={isSubmitting}
            disabled={disabled}
          >
            Reject Order
          </Button>
        </Tooltip>
        <Tooltip title="Notify the customer of any changes to the order">
          <Button
            type="primary"
            data-cy="confirm-order"
            onClick={() => updateOrder({ rejected_at: null, confirmed_at: moment().toISOString() })}
            loading={isSubmitting}
            disabled={disabled}
          >
            Confirm Order
          </Button>
        </Tooltip>
        {dropdown}
      </>
    )

    return isFloat ? (
      <FloatActionsWrapper>{content}</FloatActionsWrapper>
    ) : (
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        {content}
      </Box>
    )
  }

  const content = (
    <>
      <Tooltip title="Notify the customer of any changes to the order">
        <Button
          data-cy="update-order"
          type="primary"
          onClick={() => updateOrder({})}
          loading={isSubmitting}
          disabled={disabled || !orderDirty}
        >
          Update Order
        </Button>
      </Tooltip>
      {dropdown}
    </>
  )

  return isFloat ? (
    <FloatActionsWrapper>{content}</FloatActionsWrapper>
  ) : (
    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
      {content}
    </Box>
  )
}
